import * as Sentry from '@sentry/react';
import { Link as F7Link, Toolbar, f7ready } from 'framework7-react';
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useEntertainerEngagementContext } from '@contexts/EntertainerEngagementContext';
import { pathToRegexp } from 'path-to-regexp';

import { fetchAndUpdateApp } from '@hooks/useCapacitorUpdateAvailable';

import { openExternalUrl } from '@utilities/openExternalUrl';

export const useManagedHistory = () => {
  const [backlinkUrl, setBacklinkUrl] = useState<string>();

  useEffect(() => {
    f7ready((f7) => {
      try {
        const mainRouter = f7.views.main.router;
        const previousPath = mainRouter.history[mainRouter.history.length - 2];

        setBacklinkUrl(previousPath);
      } catch (e) {}
    });
  }, []);

  return {
    backlinkUrl,
  };
};

export let historyStack: string[] = [];

export enum EntertainerDashboardAction {
  AddLook = 'OpenPopup::AddLook',
  AddVideo = 'OpenPopup::AddVideo',
  SendBadge = 'OpenPopup::SendBadge', // Open swipe images
  MakeRec = 'OpenPopup::MakeRec',
  AddShow = 'OpenPopup::AddShow',
}

export interface NavigateOptions {
  state?: Record<string, any> & {
    back?: string;
    hideBack?: boolean;
    forceBack?: boolean;
    cityId?: string;
  };
  animate?: boolean;
  transition?: string;
}

export const useNavigate = () => {
  const { openAddLookPopup, openAddVideoPopup, openAddShowPopup, openRecommendationPopup } =
    useEntertainerEngagementContext();

  const navigate = (initialTo: string = '', options?: NavigateOptions) => {
    let to = initialTo;

    if (!to) {
      Sentry.withScope((scope) => {
        scope.setExtra('to', to);
        scope.setExtra('state', options?.state);
        scope.setExtra('options', options);
        scope.captureMessage('Empty URL');
      });
    }

    if (to === 'RELOAD_APP') {
      window.location.reload();
      return;
    }

    if (to === 'CAPACITOR_UPDATE') {
      fetchAndUpdateApp();
      return;
    }

    if (to === EntertainerDashboardAction.AddLook) {
      openAddLookPopup(options?.state);
      return;
    }

    if (to === EntertainerDashboardAction.AddVideo) {
      openAddVideoPopup(options?.state);
      return;
    }

    if (to === EntertainerDashboardAction.MakeRec) {
      // openRecommendationPopup(options?.state);
      to = '/engagement/support';
    }

    if (to === EntertainerDashboardAction.AddShow) {
      openAddShowPopup(options?.state);
      return;
    }

    if (to.startsWith('/')) {
      const { animate = true, state, transition } = options ?? {};

      f7ready((f7) => {
        const mainRouter = f7?.views?.main?.router;
        console.log(to, state);

        if (mainRouter) {
          mainRouter.navigate(to, {
            animate,
            props: state,
          });

          if (state?.back) {
            f7.views.main.router.history.pop();
            f7.views.main.router.history.push(state.back);
          }
        } else {
          console.log('Main router not found', f7.views);
          Sentry.withScope((scope) => {
            scope.setExtra('to', to);
            scope.setExtra('state', state);
            scope.setExtra('options', options);
            scope.captureMessage('Main router not found');
          });
        }
      });
    } else {
      openExternalUrl(to);
    }
  };

  return navigate;
};
export type NavigateFunction = ReturnType<typeof useNavigate>;

export const useRouter = () => {
  const navigate = useNavigate();

  const back = (url?: string) => {
    f7ready((f7) => {
      const mainRouter = f7.views.main.router;
      mainRouter.back(url, url ? { force: true } : undefined);
      // historyStack.pop();
    });
  };

  return { navigate, back };
};

export const useStoredBackUrl = () => {
  return {
    storedBackUrl: historyStack.length > 1 ? historyStack[historyStack.length - 2] : undefined,
  };
};

interface LocationContextValue<LocationState = {}> {
  pathname: string;
  previousPathName?: string;
  state: LocationState;
}
export const LocationContext = createContext<LocationContextValue>({
  pathname: '',
  state: {},
});

export const useLocation = <LocationState extends {}>() => {
  const location = useContext<LocationContextValue<LocationState>>(LocationContext as any);
  // const [state, setState] = useState<LocationState>({} as LocationState);

  // useEffect(() => {
  //   f7ready((f7) => {
  //     const mainRouter = f7.views.main.router;
  //     setState(mainRouter.currentRoute.props as LocationState);
  //   });
  // }, []);

  return location;
};

export const useParams = <T extends unknown>(): T => {
  const [params, setParams] = useState<T>({} as T);

  useEffect(() => {
    f7ready((f7) => {
      const mainRouter = f7.views.main.router;
      const currentParams = mainRouter.currentRoute.params;
      setParams(currentParams as T);
    });
  }, []);

  return params;
};

interface LinkProps {
  className?: string;
  to: string;
}
export const Link: FC<PropsWithChildren<LinkProps>> = () => {
  return <></>;
};

export const withTabbar =
  <P extends {}>(Component: FC<P>) =>
  (props: P) => {
    return (
      <>
        <Component {...props} />
        <Toolbar tabbar bottom>
          <F7Link href="/events" text="Tab 1" animate={false} />
          <F7Link href="/venues" text="Tab 2" animate={false} />
          <F7Link href="/profile" text="Tab 3" animate={false} />
        </Toolbar>
      </>
    );
  };

export const useQueryParams = <T extends {}>() => {
  const [queryParams, setQueryParams] = useState<T>({} as T);

  useEffect(() => {
    f7ready((f7) => {
      const mainRouter = f7.views.main.router;
      const currentQueryParams = mainRouter.currentRoute.query;
      setQueryParams(currentQueryParams as T);
    });
  }, []);

  return queryParams;
};

export const useHasBack = () => {
  const [hasBack, setHasBack] = useState(false);

  useEffect(() => {
    f7ready((f7) => {
      const mainRouter = f7.views.main.router;
      console.log(mainRouter);
    });
  });
  return hasBack;
};

export function routeExists(pathToCheck, routes) {
  for (const route of routes) {
    // Create a RegExp from the route's path
    const keys = [];
    const regexp = pathToRegexp(route.path, keys, { end: true });

    if (regexp.test(pathToCheck)) {
      return true;
    }

    // Recursively check child routes
    if (route.routes && route.routes.length > 0) {
      if (routeExists(pathToCheck, route.routes)) return true;
    }
  }
  return false;
}
